import { useCallback } from "react";

import {
  ENTRY_DATA_DEAL_TYPE_AND_STATUS,
  ENTRY_DATA_SECTION_KEYS,
} from "@frontend/common";

import {
  DealTypeAndStatus,
  DealTypeAndStatusKey,
  DealFilingSection,
} from "@/types/deals.types";
import { KEY_DEALS_TABLE } from "@FillInTableContainer/shared/constants/dealsKey.constants";
import {
  useTableCreatorFocusStatus,
  DEFAULT_FOCUS_STATUS,
} from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import { useTableCreatorCurrentHistory } from "@FillInTableContainer/shared/context/TableCreatorHistoryContext";
import {
  useTableCreatorPositionInfo,
  DEFAULT_POSITION_INFO,
} from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import { useEntryDataState } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import type { ArticleEntryData } from "@/types/articleEntryData.types";

interface UseHandleTableCreatorShellReturn {
  handleClickToClearDealTypeShell: () => void;
  handleKeyDownToClearDealTypeShell: (event: KeyboardEvent) => void;
}

interface PositionInfoProps {
  groupTableKey: DealFilingSection;
  groupTableIndex: number;
  groupShellKey: string | null;
  groupShellIndex: number;
  tableKey: string | null;
  tableIndex: number;
  shellKey: string;
  shellIndex: number;
}

export const useHandleDealsDealTypeShell = (
  positionPropsInfo: PositionInfoProps,
): UseHandleTableCreatorShellReturn => {
  const { changeCurrentHistory } = useTableCreatorCurrentHistory();
  const { info, changeInfo } = useTableCreatorPositionInfo();
  const { focusStatus, changeFocusStatus } = useTableCreatorFocusStatus();
  const [entryData, setEntryData] = useEntryDataState(getPressIdFromURL());

  const handleClearShell = useCallback(() => {
    const clearDealTypeShell = (): ArticleEntryData => {
      const currentDealTypeAndStatus =
        entryData?.[ENTRY_DATA_SECTION_KEYS.dealFiling]?.[
          ENTRY_DATA_DEAL_TYPE_AND_STATUS
        ];

      return {
        ...entryData,
        "Deal Filing": {
          ...entryData["Deal Filing"],
          [ENTRY_DATA_DEAL_TYPE_AND_STATUS]: {
            ...currentDealTypeAndStatus,
            [info.shellKey as DealTypeAndStatusKey]: {
              ...currentDealTypeAndStatus?.[
                info.shellKey as DealTypeAndStatusKey
              ],
              value: [],
            },
          } as DealTypeAndStatus,
        },
      };
    };

    if (info.groupTableKey === KEY_DEALS_TABLE.typeAndStatus) {
      setEntryData(clearDealTypeShell());
      changeCurrentHistory({
        entryData: clearDealTypeShell(),
        changedDataInfo: {
          target: "shell",
          action: "clear",
          position: {
            ...positionPropsInfo,
            value: null,
            valueIndex: null,
          },
        },
      });
    }

    changeFocusStatus(DEFAULT_FOCUS_STATUS);
    changeInfo(DEFAULT_POSITION_INFO);
  }, [
    changeCurrentHistory,
    changeFocusStatus,
    changeInfo,
    entryData,
    info,
    positionPropsInfo,
    setEntryData,
  ]);

  const handleClickToClearDealTypeShell = () => {
    if (focusStatus.type === "clear") {
      handleClearShell();
    }
  };

  const handleKeyDownToClearDealTypeShell = useCallback(
    (event: KeyboardEvent) => {
      if (focusStatus.type === "clear" && event.key === "Delete") {
        handleClearShell();
      }
    },
    [focusStatus.type, handleClearShell],
  );

  return {
    handleClickToClearDealTypeShell,
    handleKeyDownToClearDealTypeShell,
  };
};
